/* eslint semi: 1 */

import { PLAYER_SETUP_STEPS } from './player-setup-common';
import { PLAYER_APPLICATIONS } from './player-setup-application/player-setup-application';
import _ from 'lodash';
import { useMixpanel } from '@base-shared/composables/use-mixpanel';

import Canvas from '../player-setup/player-data-canvas';

export async function submitSerialForm () {
  const { hasError, data } = await this.$refs.pageSerial.submitForm();

  if (!hasError) {
    this.form.serial = data?.serial;
    this.isAryaPlayer = data?.isAryaPlayer || false;
    if (data?.isNewPlayer) {
      this.isNewPlayer = true;
      await this.switchPageAction(PLAYER_SETUP_STEPS.STEP_PIN);
    } else {
      // this player is already added to the account, so we need to skip PIN step
      const { error } = await this.getPlayerData();
      if (error) {
        this.validationErrors.serialForm = error;
        return null;
      }

      await this.switchPageAction(PLAYER_SETUP_STEPS.STEP_NAME);
    }

    // forcing to change screen to support onEnter event
    return null;
  }
}

export async function submitPinForm () {
  const { hasError, nextPage, data } = await this.$refs.pagePin.submitForm();
  if (!hasError) {
    this.form.serial = data.serialNumber;

    // Backend rule is to have a new request to get the updated data of the player once submitPin is successful
    const { ok: response, error } = await this.getPlayerData();
    if (error) {
      this.validationErrors.nameForm = error;
      return null;
    }

    // this is important to reset name, since it would have
    // random one assigned by backend by defualt
    this.resetPlayerBuilder(response.data, (builder) => {
      builder.getPlayer().setName('');
    });
    await this.switchPageAction(nextPage);
  }
}

export async function submitNameForm () {
  // reset validation
  this.validationErrors.nameForm = '';

  const { hasError, nextPage, data } = await this.$refs.pageName.submitForm();
  if (!hasError) {
    const { name, timezone } = data;
    this.playerBuilder.setName(name);
    this.playerBuilder.setTimezone(timezone);

    useMixpanel().track('Player name timezone updated');
    await this.switchPageAction(nextPage);
  }
}

export async function submitDisplayForm () {
  this.validationErrors.displayForm = '';

  const { hasError, nextPage, data } = await this.$refs.pageDisplay.submitForm();
  if (!hasError) {
    const { orientation, resolution, output } = data;

    this.playerBuilder.setOrientation(orientation);
    this.playerBuilder.setResolution(resolution);
    this.playerBuilder.setOutput(output);

    if (this.playerBuilder.getPlayer().getApplicationType() === PLAYER_APPLICATIONS.APP_MIRRORING) {
      this.playerBuilder.configureMirroring();
    } else {
      this.playerBuilder.configureSingleOutput();
    }

    // commit changes if it's a terminal step
    if (nextPage === PLAYER_SETUP_STEPS.CLOSE) {
      // explicitly enable Arya on the player on the last step
      this.playerBuilder.enableArya();

      const { error } = await this.commitPlayerConfiguration();
      if (error) {
        this.validationErrors.displayForm = error;
        return null;
      }
    }

    useMixpanel().track('Player resolution updated');
    await this.switchPageAction(nextPage);
  }
}

export async function submitApplicationForm () {
  const { nextPage, data } = await this.$refs.pageApplication.submitForm();

  this.playerBuilder.setApplicationType(data.application);

  useMixpanel().track('Player application selected');

  await this.switchPageAction(nextPage);
}

export async function submitArrangementForm () {
  const { nextPage, data } = await this.$refs.pageArrangement.submitForm();

  this.playerBuilder.setArrangement(data.arrangement);
  useMixpanel().track('Player application selected');

  await this.switchPageAction(nextPage);
}

export async function submitAdjustmentsForm () {
  // cleanup errors before form submit
  this.validationErrors.adjustmentsForm = '';

  const adjustmentsComponentRef = this.applicationType === PLAYER_APPLICATIONS.APP_LED ? 'pageAdjustmentsLed' : 'pageAdjustmentsVideowall';
  const canvas = new Canvas(this.$api);
  const { hasError, nextPage, data } = await this.$refs[adjustmentsComponentRef].submitForm();

  if (hasError) {
    this.validationErrors.adjustmentsForm = data;
    return;
  }

  const { data: applicationData } = data;
  let tag = applicationData.canvasTag;

  if (this.applicationType === PLAYER_APPLICATIONS.APP_VIDEOWALL) {
    const bezelsPosition = this.playerBuilder.setBezels(
      Number(applicationData.bezels.screenDiagonal),
      Number(applicationData.bezels.verticalBezel),
      Number(applicationData.bezels.horizontalBezel)
    );

    const { width: canvasWidth, height: canvasHeight } = this.playerBuilder.getPlayer().getCanvasAsObject();

    if (!canvasWidth || !canvasHeight) {
      this.validationErrors.adjustmentsForm = this.$t('playerSetup.CanvasDimensionsError');
      return;
    }

    // create new Canvas in case it is necessary
    if (!applicationData.tag) {
      const { error, ok: response } = await canvas.createVideowall(
        applicationData.canvasName,
        canvasWidth,
        canvasHeight,
        bezelsPosition
      );

      if (error) {
        this.validationErrors.adjustmentsForm = this.$t('playerSetup.CannotCreateVideowallTag');
        return;
      }

      // assign new tag name if it is just created
      tag = response.name;
    }
  } else if (this.applicationType === PLAYER_APPLICATIONS.APP_LED) {
    this.playerBuilder.setOutputPositions(applicationData.outputs);

    const { width: canvasWidth, height: canvasHeight } = applicationData.totalResolution;

    // create new Canvas in case it is necessary
    if (!applicationData.tag) {
      const { error, ok: response } = await canvas.createLED(
        applicationData.canvasName,
        canvasWidth,
        canvasHeight
      );

      if (error) {
        this.validationErrors.adjustmentsForm = this.$t('playerSetup.CannotCreateLEDTag');
        return;
      }

      // assign new tag name if it is just created
      tag = response.name;
    }
  }

  if (_.isEqual(nextPage, PLAYER_SETUP_STEPS.CLOSE)) {
    // explicitly enable Arya on the player on the last step
    this.playerBuilder.enableArya();

    await this.commitPlayerConfiguration(tag);
  }

  useMixpanel().track('Player adjustments done');
  await this.switchPageAction(nextPage);
}
